/**
 * Phase 37 — Video tab (Commit 4 MVP + Commit 5 polish).
 *
 * Mirrors the create-blog.js / create-social.js module pattern: pure
 * render functions + handler functions, called via delegation methods on
 * the CreatePage instance in create.js.
 *
 * Features:
 *   • Multi-input script entry — paste OR topic-driven AI generation.
 *   • Brand voice memory — default_voice_id auto-loads per brand,
 *     "Set as default" toggle persists the user's pick.
 *   • Cost meter — shows estimated ElevenLabs + Creatomate cost before
 *     committing, polled on script change.
 *   • Stage-labeled progress — Writing voiceover → Finding visuals →
 *     Composing slideshow → Saving → Done. Real pipeline progress polled
 *     from the async job endpoint (GET /api/faceless-video/job/:id).
 *   • Platform / aspect ratio picker — TikTok / Reels / Shorts (9:16),
 *     YouTube / Twitter (16:9), Instagram feed (1:1).
 *   • Three regenerate-variant buttons with context inputs that nudge
 *     the relevant prompt segment.
 */

import { client } from "../api/client.js";
import { escapeHtml } from "../ui/escapeHtml.js";

const VOICEOVER_MIN_CHARS = 30;
const VOICEOVER_MAX_CHARS = 4000;

const PLATFORMS = [
    { id: 'tiktok',          label: 'TikTok',           aspect: '9:16' },
    { id: 'instagram_reels', label: 'Instagram Reels',  aspect: '9:16' },
    { id: 'youtube_shorts',  label: 'YouTube Shorts',   aspect: '9:16' },
    { id: 'instagram_feed',  label: 'Instagram Feed',   aspect: '1:1'  },
    { id: 'youtube',         label: 'YouTube',          aspect: '16:9' },
    { id: 'twitter',         label: 'Twitter / X',      aspect: '16:9' },
];

function platformToAspect(platformId) {
    const p = PLATFORMS.find(x => x.id === platformId);
    return p?.aspect || '9:16';
}

// Real progress comes from polling GET /api/faceless-video/job/:id —
// the server writes live stage/percent as the pipeline advances.
const JOB_POLL_MS = 2000;
// ai_video style runs sequential Runway clips and can legitimately take
// 10+ minutes; slideshow renders are typically under two.
const JOB_POLL_BUDGET_MS = 20 * 60 * 1000;

function stageLabelFrom(progress) {
    if (!progress) return 'Working…';
    let label = progress.label || progress.stage || 'Working…';
    if (progress.clipIndex != null && progress.clipsTotal) {
        label = `Animating clip ${progress.clipIndex + 1} of ${progress.clipsTotal}…`;
    } else if (!/[….]$/.test(label)) {
        label += '…';
    }
    return label;
}

// ── Render ────────────────────────────────────────────────────────────

export function renderVideoWorkbench(page) {
    return `
      <div class="create-video-workbench" data-testid="video-editor-root">
        <main class="video-main-editor">
          ${renderVideoEditor(page)}
        </main>
        <aside class="video-sidebar-right" data-testid="video-actions-panel">
          ${renderVideoActions(page)}
        </aside>
      </div>
    `;
}

function renderVideoEditor(page) {
    const inputMode = page.videoInputMode || 'paste';
    return `
      <div class="video-card video-editor-card">
        <div class="video-editor-header">
          <span class="video-editor-label">Faceless video</span>
          <div class="video-input-mode-toggle" role="tablist" aria-label="Script input mode">
            <button class="video-mode-tab ${inputMode === 'paste' ? 'is-active' : ''}" data-video-mode="paste" data-testid="video-mode-paste" role="tab">Paste script</button>
            <button class="video-mode-tab ${inputMode === 'topic' ? 'is-active' : ''}" data-video-mode="topic" data-testid="video-mode-topic" role="tab">Write with AI</button>
          </div>
        </div>
        ${inputMode === 'topic' ? renderTopicMode(page) : renderPasteMode(page)}
        ${page.videoError ? `<div class="video-error" data-testid="video-error">${escapeHtml(page.videoError)}</div>` : ''}
        ${renderVideoResult(page)}
      </div>
    `;
}

function renderPasteMode(page) {
    const script = page.videoScript || '';
    const charCount = script.length;
    const charClass = charCount < VOICEOVER_MIN_CHARS
        ? 'video-char-count video-char-count--low'
        : charCount > VOICEOVER_MAX_CHARS
            ? 'video-char-count video-char-count--high'
            : 'video-char-count';
    return `
      <p class="t-hint" style="margin:0 0 8px;font-size:12px;color:var(--text-muted);">
        Paste a script (60-second target ≈ 150 words). Voice reads it word-for-word over a slideshow with burned-in captions.
      </p>
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:4px;">
        <span class="video-field-label" style="margin:0;">Script</span>
        <span class="${charClass}" data-testid="video-char-count">${charCount} / ${VOICEOVER_MAX_CHARS}</span>
      </div>
      <textarea
        class="video-script-input"
        data-testid="video-script-input"
        placeholder="Paste your script here. The voice will read it word-for-word."
        rows="14"
      >${escapeHtml(script)}</textarea>
    `;
}

function renderTopicMode(page) {
    const topic = page.videoTopic || '';
    const tone = page.videoToneNotes || '';
    const generating = !!page.videoScriptGenerating;
    return `
      <p class="t-hint" style="margin:0 0 8px;font-size:12px;color:var(--text-muted);">
        Give a topic; we'll draft a 60-second script in your brand voice. You can edit it before generating the video.
      </p>
      <label class="video-field-label">Topic</label>
      <input
        class="video-topic-input"
        data-testid="video-topic-input"
        type="text"
        placeholder="e.g. 3 mistakes new founders make when raising"
        value="${escapeHtml(topic)}"
        style="width:100%;padding:10px 12px;border:1px solid var(--border, rgba(0,0,0,0.12));border-radius:8px;font-size:14px;margin-bottom:10px;"
      />
      <label class="video-field-label">Tone notes <span style="font-weight:400;color:var(--text-muted);">(optional)</span></label>
      <input
        class="video-tone-input"
        data-testid="video-tone-input"
        type="text"
        placeholder="e.g. punchy, slightly sarcastic, no jargon"
        value="${escapeHtml(tone)}"
        style="width:100%;padding:10px 12px;border:1px solid var(--border, rgba(0,0,0,0.12));border-radius:8px;font-size:14px;margin-bottom:12px;"
      />
      <button
        class="btn-glass video-script-ai-btn"
        data-action="video-write-script"
        data-testid="video-write-script-btn"
        ${(topic.trim().length > 0 && !generating) ? '' : 'disabled'}
        style="width:100%;padding:10px 14px;"
      >
        ${generating ? `<span class="video-spinner" aria-hidden="true"></span> Drafting…` : 'Write Script with AI'}
      </button>
      ${page.videoScript ? `
        <div style="margin-top:14px;">
          <div style="display:flex;justify-content:space-between;align-items:center;">
            <label class="video-field-label">Draft (editable)</label>
            <span class="video-char-count" data-testid="video-char-count">${(page.videoScript || '').length} / ${VOICEOVER_MAX_CHARS}</span>
          </div>
          <textarea
            class="video-script-input"
            data-testid="video-script-input"
            rows="11"
            style="min-height:240px;"
          >${escapeHtml(page.videoScript)}</textarea>
        </div>
      ` : ''}
    `;
}

function renderVideoResult(page) {
    const r = page.videoResult;
    if (!r) return '';
    const ctx = page.videoRegenContext || { voice: '', visuals: '', script: '' };
    return `
      <div class="video-result" data-testid="video-result">
        <h3 style="margin:16px 0 8px;font-size:14px;">Your video</h3>
        <video controls preload="metadata" src="${escapeHtml(r.url || '')}" style="width:100%;max-width:480px;border-radius:8px;background:#000;"></video>
        <div style="margin-top:8px;font-size:12px;color:var(--text-muted);">
          ${r.duration_seconds ? `${r.duration_seconds}s · ` : ''}${r.width && r.height ? `${r.width}×${r.height} · ` : ''}saved to Library${r.costCents != null ? ` · est. cost $${(r.costCents / 100).toFixed(2)}` : ''}
        </div>
        ${r.visualsWarning ? `<div class="video-result-warn" data-testid="video-visuals-warning" style="margin-top:8px;font-size:12px;color:var(--warning, #b45309);">⚠ ${escapeHtml(r.visualsWarning)}</div>` : ''}
        ${r.saveWarning ? `<div class="video-result-warn" data-testid="video-save-warning" style="margin-top:4px;font-size:12px;color:var(--warning, #b45309);">⚠ ${escapeHtml(r.saveWarning)}</div>` : ''}
        <div class="video-result-actions" style="display:flex;gap:8px;flex-wrap:wrap;margin-top:12px;">
          <a class="btn-glass btn-sm" href="${escapeHtml(r.url || '')}" download="${escapeHtml(`faceless-video-${r.libraryId || 'render'}.mp4`)}" data-testid="video-download-btn">Download MP4</a>
          ${page.videoDraftCreated ? `
            <a class="btn-glass btn-sm" href="/#library" data-testid="video-draft-created-link">Draft created ✓ — view in Library</a>
          ` : `
            <button class="btn-glass btn-sm" data-action="video-create-draft" data-testid="video-create-draft-btn" ${page.videoDraftCreating ? 'disabled' : ''}>
              ${page.videoDraftCreating ? 'Creating draft…' : `Create ${escapeHtml(PLATFORMS.find(p => p.id === (page.videoPlatform || PLATFORMS[0].id))?.label || 'post')} draft`}
            </button>
          `}
        </div>
        <div class="video-regen-block" data-testid="video-regen-block" style="margin-top:14px;">
          <div class="video-field-label" style="margin-bottom:6px;">Regenerate with changes</div>
          ${renderRegenRow('voice',   'Voice',          'e.g. slower, more energetic', ctx.voice)}
          ${renderRegenRow('visuals', 'Visuals',        'e.g. more colorful, less corporate', ctx.visuals)}
          ${renderRegenRow('script',  'Script + tone',  'e.g. punchier intro, drop jargon', ctx.script)}
        </div>
      </div>
    `;
}

function renderLibraryPicker(page) {
    const loading = !!page.videoLibraryLoading;
    const items = Array.isArray(page.videoLibraryImages) ? page.videoLibraryImages : [];
    const selected = Array.isArray(page.videoSelectedImageUrls) ? page.videoSelectedImageUrls : [];
    const max = 12;

    if (loading) {
        return `<div class="video-library-empty" data-testid="video-library-loading">Loading your library…</div>`;
    }
    if (items.length === 0) {
        return `
          <div class="video-library-empty" data-testid="video-library-empty">
            No images in your library yet. Upload some on the
            <a href="/#library">Library</a> page, then come back.
          </div>
        `;
    }
    return `
      <div class="video-library-picker" data-testid="video-library-picker">
        <div class="video-library-picker__hint">
          Click images to add them to the slideshow. Numbered badges show play order.
          <span class="video-library-picker__count" data-testid="video-library-count">
            ${selected.length} of ${max} selected
          </span>
        </div>
        <div class="video-library-grid">
          ${items.slice(0, 60).map(m => {
            const idx = selected.indexOf(m.url);
            const isSelected = idx >= 0;
            return `
              <button
                type="button"
                class="video-library-thumb ${isSelected ? 'is-selected' : ''}"
                data-action="video-library-toggle"
                data-library-url="${escapeHtml(m.url)}"
                data-testid="video-library-thumb"
                aria-pressed="${isSelected}"
                title="${escapeHtml(m.originalName || m.filename || '')}"
              >
                <img src="${escapeHtml(m.thumbUrl || m.url)}" alt="" loading="lazy" />
                ${isSelected ? `<span class="video-library-thumb__badge">${idx + 1}</span>` : ''}
              </button>
            `;
          }).join('')}
        </div>
        ${selected.length === 0 ? `
          <p class="video-library-hint-warn" data-testid="video-library-hint-warn">
            Pick at least 1 image to enable Generate.
          </p>
        ` : ''}
      </div>
    `;
}

function renderRegenRow(key, label, placeholder, value) {
    return `
      <div class="video-regen-row" data-regen-key="${key}">
        <input
          class="video-regen-context"
          data-testid="video-regen-context-${key}"
          data-regen-input="${key}"
          type="text"
          placeholder="${escapeHtml(placeholder)}"
          value="${escapeHtml(value || '')}"
        />
        <button
          class="btn-glass btn-sm video-regen-btn"
          data-action="video-regen"
          data-regen-key="${key}"
          data-testid="video-regen-btn-${key}"
        >Regen ${label.toLowerCase()}</button>
      </div>
    `;
}

function renderVideoActions(page) {
    const voices = Array.isArray(page.videoVoices) ? page.videoVoices : [];
    const voicesLoading = !!page.videoVoicesLoading;
    const voicesError = page.videoVoicesError;
    const generating = !!page.videoGenerating;
    const scriptLen = (page.videoScript || '').length;
    const usingLibrary = page.videoImageSource === 'library';
    const selectedCount = (page.videoSelectedImageUrls || []).length;
    const canGenerate = scriptLen >= VOICEOVER_MIN_CHARS
        && !!page.videoVoiceId
        && !generating
        && (!usingLibrary || selectedCount > 0);
    const isDefault = page.videoVoiceIsBrandDefault;
    const platform = page.videoPlatform || PLATFORMS[0].id;
    const est = page.videoCostEstimate;
    const stage = page.videoStageLabel || (generating ? 'Working…' : '');
    const pct = page.videoStagePercent != null ? page.videoStagePercent : (generating ? 5 : 0);

    return `
      <div class="video-card video-actions-card">

        <div class="video-actions-section">
          <label class="video-field-label">Platform</label>
          <select class="video-platform-select" data-testid="video-platform-select">
            ${PLATFORMS.map(p => `<option value="${p.id}" ${p.id === platform ? 'selected' : ''}>${escapeHtml(p.label)} (${p.aspect})</option>`).join('')}
          </select>
        </div>

        <div class="video-actions-section">
          <div style="display:flex;justify-content:space-between;align-items:center;">
            <label class="video-field-label" style="margin:0;">Voice</label>
            ${page.videoVoiceId && voices.length > 0 ? `
              <button class="video-set-default-link" data-action="video-set-default-voice" data-testid="video-set-default-voice" type="button">
                ${isDefault ? 'Brand default ✓' : 'Set as brand default'}
              </button>
            ` : ''}
          </div>
          ${voicesLoading ? `
            <div class="video-voice-loading" data-testid="video-voices-loading">Loading voices…</div>
          ` : voicesError ? `
            <div class="video-error" data-testid="video-voices-error">${escapeHtml(voicesError)}</div>
            <button class="btn-glass btn-sm" data-action="video-retry-voices" data-testid="video-retry-voices">Retry</button>
          ` : voices.length === 0 ? `
            <div class="video-voice-empty" data-testid="video-voices-empty">
              No voices yet. <a href="/#settings?tab=ai">Connect ElevenLabs</a> in AI Settings.
            </div>
          ` : `
            <select class="video-voice-select" data-testid="video-voice-select">
              <option value="">Pick a voice…</option>
              ${voices.map(v => `<option value="${escapeHtml(v.voice_id)}" ${page.videoVoiceId === v.voice_id ? 'selected' : ''}>${escapeHtml(v.name)}${v.category ? ` — ${escapeHtml(v.category)}` : ''}</option>`).join('')}
            </select>
          `}
        </div>

        <div class="video-actions-section">
          <label class="video-field-label">Visual style</label>
          <select class="video-visual-style-select" data-testid="video-visual-style-select">
            <option value="slideshow" ${(page.videoVisualStyle || 'slideshow') === 'slideshow' ? 'selected' : ''}>Slideshow (fast, cheap)</option>
            <option value="ai_video" ${page.videoVisualStyle === 'ai_video' ? 'selected' : ''}>AI video — Runway (premium, pricier)</option>
          </select>
          ${page.videoVisualStyle === 'ai_video' ? `
            <div class="video-style-warn" data-testid="video-style-warn" role="alert">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
              <span><strong>Costs ~10–15× more than slideshow.</strong> Runway charges per-second per clip; a 60s video is ~6 clips × 5s ≈ ~$1.50 in Runway charges alone. Confirm your Runway BYOK budget before generating.</span>
            </div>
          ` : ''}
        </div>

        <div class="video-actions-section">
          <label class="video-field-label">${page.videoVisualStyle === 'ai_video' ? 'Source frames' : 'Visuals'}</label>
          <select class="video-image-source-select" data-testid="video-image-source-select">
            <option value="stock" ${(page.videoImageSource || 'stock') === 'stock' ? 'selected' : ''}>Stock photos (auto)</option>
            <option value="ai" ${page.videoImageSource === 'ai' ? 'selected' : ''}>AI-generated images (on-brand, slower)</option>
            <option value="library" ${page.videoImageSource === 'library' ? 'selected' : ''}>Pick from my library</option>
          </select>
          ${page.videoImageSource === 'library' ? renderLibraryPicker(page) : ''}
        </div>

        ${est ? `
          <details class="video-cost-meter" data-testid="video-cost-meter"${page.videoCostExpanded ? ' open' : ''}>
            <summary class="video-cost-meter__summary" data-testid="video-cost-meter-summary">
              <span>Cost estimate</span>
              <svg class="video-cost-meter__chev" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="6 9 12 15 18 9"/></svg>
            </summary>
            <div class="video-cost-meter__body">
              <div class="video-cost-meter__row">
                <span>Voiceover (${est.assumptions?.chars || scriptLen} chars)</span>
                <span>$${(est.voiceoverCents / 100).toFixed(2)}</span>
              </div>
              <div class="video-cost-meter__row">
                <span>Render (~${est.assumptions?.durationSeconds || 60}s)</span>
                <span>$${(est.renderCents / 100).toFixed(2)}</span>
              </div>
              ${est.aiVideoCents > 0 ? `
              <div class="video-cost-meter__row" data-testid="video-cost-ai-video-row">
                <span>AI video clips (${est.assumptions?.clipCount || 6} × ${est.assumptions?.clipDuration || 5}s, Runway)</span>
                <span>$${(est.aiVideoCents / 100).toFixed(2)}</span>
              </div>
              ` : ''}
              <div class="video-cost-meter__total">
                <span>Estimated total</span>
                <span>$${(est.totalCents / 100).toFixed(2)}</span>
              </div>
            </div>
          </details>
        ` : ''}

        <div class="video-actions-section">
          <button
            class="btn-primary video-generate-btn"
            data-action="video-generate"
            data-testid="video-generate-btn"
            ${canGenerate ? '' : 'disabled'}
          >
            ${generating ? `
              <span class="video-spinner" aria-hidden="true"></span>
              Rendering…
            ` : 'Generate Video'}
          </button>
          ${generating ? `
            <div class="video-progress-bar" data-testid="video-progress-bar">
              <div class="video-progress-bar__fill" style="width:${pct}%;"></div>
            </div>
            <div class="video-progress-hint" data-testid="video-progress-hint">${escapeHtml(stage)}</div>
          ` : ''}
        </div>

        ${page.videoResult ? `
          <div class="video-actions-section">
            <button class="btn-glass" data-action="video-reset" data-testid="video-reset-btn">Make another</button>
          </div>
        ` : ''}
      </div>
    `;
}

// ── Handlers ──────────────────────────────────────────────────────────

export async function loadVoices(page) {
    if (page.videoVoicesLoading) return;
    page.videoVoicesLoading = true;
    page.videoVoicesError = null;
    page.render();
    try {
        const res = await client.listFacelessVideoVoices();
        page.videoVoices = Array.isArray(res?.voices) ? res.voices : [];
        // Try to load the brand-default voice memory, then auto-select.
        let defaultVoiceId = null;
        try {
            const brandId = page.activeBrandId || null;
            const def = await client.getDefaultVideoVoice(brandId);
            defaultVoiceId = def?.voice_id || null;
        } catch (_) { /* ignore — default voice is QoL only */ }
        if (defaultVoiceId && page.videoVoices.some(v => v.voice_id === defaultVoiceId)) {
            page.videoVoiceId = defaultVoiceId;
            page.videoVoiceIsBrandDefault = true;
        } else if (!page.videoVoiceId && page.videoVoices.length > 0) {
            page.videoVoiceId = page.videoVoices[0].voice_id;
            page.videoVoiceIsBrandDefault = false;
        }
    } catch (err) {
        page.videoVoices = [];
        page.videoVoicesError = err?.body?.error || err?.body?.message || err.message || 'Could not load voices.';
        if (err?.body?.code === 'NO_ELEVENLABS_KEY') {
            page.videoVoicesError = 'Connect your ElevenLabs key in Settings → AI Settings to load voices.';
        }
    } finally {
        page.videoVoicesLoading = false;
        page.render();
    }
}

export async function handleScriptGenerate(page) {
    const topic = (page.videoTopic || '').trim();
    if (!topic) return;
    if (page.videoScriptGenerating) return;
    page.videoScriptGenerating = true;
    page.videoError = null;
    page.render();
    try {
        const res = await client.generateVideoScript({
            topic,
            tone_notes: (page.videoToneNotes || '').trim() || undefined,
            brandId: page.activeBrandId || undefined,
        });
        if (res?.ok && res.script) {
            page.videoScript = res.script;
            scheduleCostEstimate(page);
        } else {
            page.videoError = res?.error || 'Could not draft the script. Try again.';
        }
    } catch (err) {
        const body = err?.body || {};
        page.videoError = body.error || body.message || err.message || 'Script generation failed.';
    } finally {
        page.videoScriptGenerating = false;
        page.render();
    }
}

export async function handleSetVoiceAsDefault(page) {
    if (!page.videoVoiceId || !page.activeBrandId) return;
    try {
        await client.setDefaultVideoVoice({
            brandId: page.activeBrandId,
            voice_id: page.videoVoiceId,
        });
        page.videoVoiceIsBrandDefault = true;
        page.render();
    } catch (err) {
        page.videoError = err?.body?.error || err.message || 'Could not save default voice.';
        page.render();
    }
}

export async function handleVideoGenerate(page, opts = {}) {
    if (page.videoGenerating) return;
    const script = (page.videoScript || '').trim();
    if (script.length < VOICEOVER_MIN_CHARS) {
        page.videoError = `Script is too short (${script.length} chars). Aim for at least ${VOICEOVER_MIN_CHARS}.`;
        page.render();
        return;
    }
    if (!page.videoVoiceId) {
        page.videoError = 'Pick a voice first.';
        page.render();
        return;
    }
    page.videoGenerating = true;
    page.videoError = null;
    page.videoStageLabel = 'Starting…';
    page.videoStagePercent = 2;
    page.render();

    try {
        const visualContextRaw = (opts.visualContext || '').trim() || (page.videoRegenContext?.visuals || '').trim();
        const usingLibrary = page.videoImageSource === 'library';
        const handPicked = Array.isArray(page.videoSelectedImageUrls) ? page.videoSelectedImageUrls : [];
        const submit = await client.generateFacelessVideoAsync({
            script,
            voice_id: page.videoVoiceId,
            visual_style: page.videoVisualStyle || 'slideshow',
            aspect: platformToAspect(page.videoPlatform),
            image_source: page.videoImageSource || 'stock',
            image_urls: usingLibrary && handPicked.length > 0 ? handPicked : undefined,
            visual_context: visualContextRaw || undefined,
            voice_settings: opts.voice_settings || undefined,
            voice_note: opts.voice_note || undefined,
            brandId: page.activeBrandId || undefined,
            template_id: opts.template_id || undefined,
        });
        if (!submit?.ok || !submit.job_id) {
            page.videoError = submit?.error || 'Could not start the render. Try again.';
            return;
        }

        const job = await pollVideoJob(page, submit.job_id);
        if (job?.status === 'succeeded' && job.result?.url) {
            page.videoDraftCreated = null;
            page.videoResult = {
                url: job.result.url,
                libraryId: job.result.libraryId,
                duration_seconds: job.result.duration_seconds,
                width: job.result.width,
                height: job.result.height,
                costCents: job.result.costCents,
                saveWarning: job.result.saveWarning || null,
                visualsWarning: job.result.visualsWarning || null,
            };
            page.videoStageLabel = 'Done!';
            page.videoStagePercent = 100;
        } else if (job?.status === 'failed') {
            const e = job.error || {};
            if (e.code === 'NO_ELEVENLABS_KEY' || e.code === 'NO_CREATOMATE_KEY' || e.code === 'NO_RUNWAY_KEY') {
                const provider = e.code === 'NO_ELEVENLABS_KEY' ? 'ElevenLabs'
                    : e.code === 'NO_CREATOMATE_KEY' ? 'Creatomate' : 'Runway';
                page.videoError = `Missing API key. Connect it in Settings → AI Settings (${provider}).`;
            } else {
                page.videoError = e.message || 'Generation failed. Try again.';
            }
        } else {
            // Poll budget exhausted — the job may still finish server-side.
            page.videoError = 'This render is taking unusually long. It may still finish — check your Library in a few minutes before retrying.';
        }
    } catch (err) {
        const body = err?.body || {};
        if (body.code === 'NO_ELEVENLABS_KEY' || body.code === 'NO_CREATOMATE_KEY' || body.code === 'NO_RUNWAY_KEY') {
            const provider = body.code === 'NO_ELEVENLABS_KEY' ? 'ElevenLabs'
                : body.code === 'NO_CREATOMATE_KEY' ? 'Creatomate' : 'Runway';
            page.videoError = `Missing API key. Connect it in Settings → AI Settings (${provider}).`;
        } else {
            page.videoError = body.error || body.message || err.message || 'Generation failed.';
        }
    } finally {
        page.videoGenerating = false;
        page.render();
    }
}

/**
 * Poll the async job until it reaches a terminal state, painting real
 * stage/percent progress along the way. Returns the final job (or the
 * last seen one if the poll budget runs out). Transient poll failures
 * are tolerated — the next tick retries.
 */
async function pollVideoJob(page, jobId) {
    const startedAt = Date.now();
    let lastPaint = '';
    let job = null;
    while (Date.now() - startedAt < JOB_POLL_BUDGET_MS) {
        await new Promise(r => setTimeout(r, JOB_POLL_MS));
        try {
            const res = await client.getFacelessVideoJob(jobId);
            job = res?.job || job;
        } catch (_) { continue; }
        if (!job) continue;
        const prog = job.progress || {};
        const label = stageLabelFrom(prog);
        const paintKey = `${label}|${prog.percent}`;
        if (paintKey !== lastPaint) {
            lastPaint = paintKey;
            page.videoStageLabel = label;
            if (prog.percent != null) page.videoStagePercent = prog.percent;
            page.render();
        }
        if (job.status === 'succeeded' || job.status === 'failed') return job;
    }
    return job;
}

export async function handleRegenerate(page, key) {
    if (!key || !page.videoRegenContext) return;
    const ctx = (page.videoRegenContext[key] || '').trim();
    if (key === 'script') {
        // Script note → a real LLM revision of the current script. The
        // revised text lands in the visible textarea before the re-render
        // so the user can see (and further edit) what changed.
        if (ctx) {
            if (page.videoScriptGenerating) return;
            page.videoScriptGenerating = true;
            page.videoError = null;
            page.render();
            try {
                const res = await client.generateVideoScript({
                    revise_script: true,
                    current_script: page.videoScript || '',
                    revision_note: ctx,
                    brandId: page.activeBrandId || undefined,
                });
                if (res?.ok && res.script) {
                    page.videoScript = res.script;
                    scheduleCostEstimate(page);
                } else {
                    page.videoError = res?.error || 'Could not revise the script. Try again.';
                    return;
                }
            } catch (err) {
                page.videoError = err?.body?.error || err.message || 'Script revision failed.';
                return;
            } finally {
                page.videoScriptGenerating = false;
                page.render();
            }
        }
        await handleVideoGenerate(page);
    } else if (key === 'visuals') {
        // Visuals note → image search / AI-image prompt context.
        await handleVideoGenerate(page, { visualContext: ctx });
    } else if (key === 'voice') {
        // Voice note → server maps it to ElevenLabs voice_settings
        // (stability / style / speed) before synthesizing the re-take.
        await handleVideoGenerate(page, { voice_note: ctx || undefined });
    }
}

export function handleVideoReset(page) {
    page.videoResult = null;
    page.videoError = null;
    page.videoDraftCreated = null;
    page.videoDraftCreating = false;
    page.videoRegenContext = { voice: '', visuals: '', script: '' };
    page.render();
}

/**
 * "Create draft" from the Video tab result — saves a content_items draft
 * on the selected platform with the rendered video attached, mirroring
 * the media_json shape the batch derivative path writes.
 */
export async function handleCreateDraftFromVideo(page) {
    const r = page.videoResult;
    if (!r?.url || page.videoDraftCreating) return;
    page.videoDraftCreating = true;
    page.videoError = null;
    page.render();
    try {
        const res = await client.saveToLibrary({
            items: [{
                platform: page.videoPlatform || PLATFORMS[0].id,
                content: (page.videoScript || '').trim(),
                title: 'Faceless video',
                media: [{
                    type: 'video',
                    url: r.url,
                    mediaId: r.libraryId || null,
                    source: 'ai:faceless',
                }],
            }],
        });
        if (!res?.success) {
            page.videoError = res?.error || 'Could not create the draft.';
            return;
        }
        page.videoDraftCreated = { id: Array.isArray(res.ids) ? res.ids[0] : null };
    } catch (err) {
        page.videoError = err?.body?.error || err.message || 'Could not create the draft.';
    } finally {
        page.videoDraftCreating = false;
        page.render();
    }
}

export async function loadLibraryImages(page) {
    if (page.videoLibraryLoading) return;
    page.videoLibraryLoading = true;
    page.videoLibraryImages = page.videoLibraryImages || [];
    page.render();
    try {
        const res = await client.getWorkspaceMedia({
            brand_id: page.activeBrandId || '',
            approved_only: '1',
        });
        // Only images — videos/audio can't be slides.
        page.videoLibraryImages = (res?.media || []).filter(m =>
            typeof m.fileType === 'string' && m.fileType.startsWith('image/')
        );
    } catch (err) {
        page.videoLibraryImages = [];
        page.videoError = err?.body?.error || err.message || 'Could not load library.';
    } finally {
        page.videoLibraryLoading = false;
        page.render();
    }
}

export function toggleLibraryImage(page, url) {
    if (!url) return;
    if (!Array.isArray(page.videoSelectedImageUrls)) page.videoSelectedImageUrls = [];
    const idx = page.videoSelectedImageUrls.indexOf(url);
    const max = 12;
    if (idx >= 0) {
        page.videoSelectedImageUrls.splice(idx, 1);
    } else if (page.videoSelectedImageUrls.length < max) {
        page.videoSelectedImageUrls.push(url);
    }
    page.render();
}

let _costEstimateTimer = null;
export function scheduleCostEstimate(page) {
    if (_costEstimateTimer) clearTimeout(_costEstimateTimer);
    _costEstimateTimer = setTimeout(async () => {
        const chars = (page.videoScript || '').length;
        if (chars < VOICEOVER_MIN_CHARS) {
            page.videoCostEstimate = null;
            page.render();
            return;
        }
        try {
            const est = await client.estimateFacelessVideoCost(chars, 60, {
                style: page.videoVisualStyle || 'slideshow',
                clipCount: 6,
                clipDuration: 5,
            });
            if (est?.ok) {
                page.videoCostEstimate = est;
                page.render();
            }
        } catch (_) { /* silent — cost meter is a nice-to-have */ }
    }, 400);
}

// ── Input binding ────────────────────────────────────────────────────

export function bindVideoInputs(page) {
    const root = page.container;
    if (!root) return;
    if (!page.videoRegenContext) page.videoRegenContext = { voice: '', visuals: '', script: '' };

    // Script textarea (live char count + cost meter)
    const script = root.querySelector('[data-testid="video-script-input"]');
    if (script) {
        script.addEventListener('input', (e) => {
            page.videoScript = e.target.value;
            const counter = root.querySelector('[data-testid="video-char-count"]');
            if (counter) counter.textContent = `${e.target.value.length} / ${VOICEOVER_MAX_CHARS}`;
            scheduleCostEstimate(page);
        });
    }

    // Topic + tone inputs. The listener is deliberately render-free (a
    // re-render would drop input focus), so flip the Write Script button's
    // disabled state in place — it's computed at render time and typing
    // alone never re-rendered, leaving the button permanently disabled.
    const topic = root.querySelector('[data-testid="video-topic-input"]');
    if (topic) topic.addEventListener('input', (e) => {
        page.videoTopic = e.target.value;
        const writeBtn = root.querySelector('[data-testid="video-write-script-btn"]');
        if (writeBtn) writeBtn.disabled = e.target.value.trim().length === 0 || !!page.videoScriptGenerating;
    });
    const tone = root.querySelector('[data-testid="video-tone-input"]');
    if (tone) tone.addEventListener('input', (e) => { page.videoToneNotes = e.target.value; });

    // Mode toggle
    root.querySelectorAll('[data-video-mode]').forEach(btn => {
        btn.addEventListener('click', () => {
            const mode = btn.dataset.videoMode;
            if (mode !== page.videoInputMode) {
                page.videoInputMode = mode;
                page.render();
            }
        });
    });

    // Platform / aspect ratio
    const platform = root.querySelector('[data-testid="video-platform-select"]');
    if (platform) {
        platform.addEventListener('change', (e) => {
            page.videoPlatform = e.target.value || PLATFORMS[0].id;
        });
    }

    // Voice select (Set as default tracking)
    const voice = root.querySelector('[data-testid="video-voice-select"]');
    if (voice) {
        voice.addEventListener('change', (e) => {
            page.videoVoiceId = e.target.value || null;
            page.videoVoiceIsBrandDefault = false;
            page.render();
        });
    }

    // Visual Style — re-fetch cost estimate immediately so the user sees
    // the price jump (or drop) the moment they switch styles.
    const styleSel = root.querySelector('[data-testid="video-visual-style-select"]');
    if (styleSel) {
        styleSel.addEventListener('change', (e) => {
            page.videoVisualStyle = e.target.value || 'slideshow';
            page.render();
            scheduleCostEstimate(page);
        });
    }

    // Image source — switching to library kicks off the picker fetch
    const imgSrc = root.querySelector('[data-testid="video-image-source-select"]');
    if (imgSrc) {
        imgSrc.addEventListener('change', (e) => {
            const nextSource = e.target.value || 'stock';
            page.videoImageSource = nextSource;
            page.render();
            if (nextSource === 'library' && (!Array.isArray(page.videoLibraryImages) || page.videoLibraryImages.length === 0)) {
                loadLibraryImages(page);
            }
        });
    }

    // Library thumbnail toggles (selection order)
    root.querySelectorAll('[data-action="video-library-toggle"]').forEach(btn => {
        btn.addEventListener('click', (e) => {
            e.preventDefault();
            const url = e.currentTarget.dataset.libraryUrl;
            toggleLibraryImage(page, url);
        });
    });

    // Cost-meter open/closed state persists across visits so user prefs stick
    const cost = root.querySelector('[data-testid="video-cost-meter"]');
    if (cost) {
        cost.addEventListener('toggle', () => {
            page.videoCostExpanded = !!cost.open;
            try { localStorage.setItem('heist_video_cost_expanded', cost.open ? '1' : '0'); } catch (_) {}
        });
    }

    // Buttons
    root.querySelector('[data-action="video-generate"]')
        ?.addEventListener('click', () => handleVideoGenerate(page));
    root.querySelector('[data-action="video-reset"]')
        ?.addEventListener('click', () => handleVideoReset(page));
    root.querySelector('[data-action="video-create-draft"]')
        ?.addEventListener('click', () => handleCreateDraftFromVideo(page));
    root.querySelector('[data-action="video-retry-voices"]')
        ?.addEventListener('click', () => loadVoices(page));
    root.querySelector('[data-action="video-write-script"]')
        ?.addEventListener('click', () => handleScriptGenerate(page));
    root.querySelector('[data-action="video-set-default-voice"]')
        ?.addEventListener('click', () => handleSetVoiceAsDefault(page));

    // Regenerate context inputs
    root.querySelectorAll('[data-regen-input]').forEach(input => {
        input.addEventListener('input', (e) => {
            const key = e.target.dataset.regenInput;
            if (!page.videoRegenContext) page.videoRegenContext = { voice: '', visuals: '', script: '' };
            page.videoRegenContext[key] = e.target.value;
        });
    });

    // Regenerate buttons
    root.querySelectorAll('[data-action="video-regen"]').forEach(btn => {
        btn.addEventListener('click', (e) => {
            const key = e.currentTarget.dataset.regenKey;
            handleRegenerate(page, key);
        });
    });
}
